import * as moment from 'moment';
import * as DateTimeModule from 'react-datetime';
import { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import usePrevious from '@nkr/services/use-previous';
import {
    IValidationResult,
    STORAGE_FORMAT,
    defaultSerologyDate,
    validateSerologyDate
} from '@nkr/utils/blood-draw-validation';
import { styles } from './styles';

const DateTime = (DateTimeModule as any).default || DateTimeModule;

// This modal reads and writes MM/DD/YY - the format the pending-swaps payload uses and the format
// the serology order endpoint is given. The shared validator works in ISO, so the surgery date is
// converted once on the way in and the seed converted back on the way out.
const DISPLAY_FORMAT = 'MM/DD/YY';

interface IProps {
    show: boolean,
    surgeryDate: string,
    isInternalOrRemote: boolean,
    onSend: (bloodDrawDate: string) => void,
    onCancel: () => void
}

const RequestSerologyKit = (props: IProps) => {
    const [bloodDrawDate, setBloodDrawDate] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const previousShow = usePrevious(props.show);

    const surgeryDate = props.surgeryDate
        ? moment(props.surgeryDate, DISPLAY_FORMAT).format(STORAGE_FORMAT)
        : '';

    const validatePreOpDate = (date: moment.Moment): IValidationResult => {
        const result = validateSerologyDate(date, '');

        if (result.isValid && surgeryDate && date.isSameOrAfter(surgeryDate, 'day')) {
            return { isValid: false, error: 'Serology blood draw must be before the donor surgery date.' };
        }

        return result;
    };

    useEffect(() => {
        if (previousShow == false && props.show == true) {
            const seed = props.isInternalOrRemote ? defaultSerologyDate('') : '';

            setBloodDrawDate(seed ? moment(seed, STORAGE_FORMAT).format(DISPLAY_FORMAT) : '');
            setErrorMessage('');
        }
    }, [props.show]);

    const validate = (dateToValidate: string): string => {
        if (!dateToValidate || dateToValidate.trim() === '') {
            return 'Blood draw date is required.';
        }

        const date = moment(dateToValidate, DISPLAY_FORMAT, true);

        if (!date.isValid()) {
            return 'Please specify a valid blood draw date.';
        }

        return validatePreOpDate(date).error;
    };

    const isValidDate = (currentDate: moment.Moment): boolean => {
        return validatePreOpDate(currentDate).isValid;
    };

    const validationError = props.isInternalOrRemote ? validate(bloodDrawDate) : '';

    const handleBloodDrawDateChange = (value: moment.Moment | string) => {
        const newBloodDrawDate = moment.isMoment(value) ? value.format(DISPLAY_FORMAT) : value;

        setBloodDrawDate(newBloodDrawDate);
        setErrorMessage(validate(newBloodDrawDate));
    };

    const handleSend = () => {
        props.onSend(props.isInternalOrRemote ? bloodDrawDate : '');
    };

    const handleCancel = () => {
        props.onCancel();
    };

    return (
        <Modal show={props.show} onHide={handleCancel}>
            <Modal.Header closeButton>
                <Modal.Title>Emergency Message Details</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div style={styles.bodyText}>
                    Click the send button below if you are sure you wish to utilize the NKR Streamlined Serology Service for
                    your donor. The recipient center will be billed for this service.
                </div>

                {props.isInternalOrRemote && (
                    <div style={styles.dateRow}>
                        <label style={styles.dateLabel}>Scheduled Blood Draw Date:</label>
                        <div style={styles.datePicker}>
                            <DateTime
                                value={bloodDrawDate}
                                dateFormat={DISPLAY_FORMAT}
                                timeFormat={false}
                                strictParsing={true}
                                closeOnSelect={true}
                                isValidDate={isValidDate}
                                inputProps={{ className: 'form-control' }}
                                onChange={handleBloodDrawDateChange}
                            />
                        </div>
                    </div>
                )}

                {props.isInternalOrRemote && errorMessage !== '' && (
                    <div className='alert alert-danger text-center' style={styles.validationAlert}>{errorMessage}</div>
                )}

                <div className='alert alert-warning'>
                    Serology Kit Requests after 4pm Eastern will go out next day. If it is before 4:00pm Eastern, then the
                    kit should ship today and arrive the next business morning if requested now.
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button bsStyle='primary' onClick={handleSend} disabled={props.isInternalOrRemote && validationError !== ''}>Send Serology Kit</Button>
                <Button onClick={handleCancel}>Cancel</Button>
            </Modal.Footer>
        </Modal>
    );
}

export default RequestSerologyKit;
